/**
 * VM module route definitions.
 *
 * Exports lazy-loaded page components and route paths for the
 * Vendor Management module. Consumed by the dynamic module route
 * loader in routes/index.tsx via import.meta.glob.
 *
 * Cross-References:
 *   - FR-VM-001: Vendor CRUD
 *   - FR-VM-003: Contract CRUD
 *   - FR-VM-005: Procurement workflows
 *   - Sprint VM1: Vendor Management Foundation
 */
import { lazy } from 'react';

const VendorList = lazy(() => import('./pages/VendorList'));
const VendorDetail = lazy(() => import('./pages/VendorDetail'));
const VendorIntake = lazy(() => import('./pages/VendorIntake'));
const MyVendors = lazy(() => import('./pages/MyVendors'));
const ContractList = lazy(() => import('./pages/ContractList'));
const ContractCreate = lazy(() => import('./pages/ContractCreate'));
const ContractDetail = lazy(() => import('./pages/ContractDetail'));
const ProcurementQueue = lazy(() => import('./pages/ProcurementQueue'));
const DataClassifications = lazy(() => import('./pages/DataClassifications'));
const ClassificationCadenceAdmin = lazy(
  () => import('./pages/ClassificationCadenceAdmin'),
);
const ContractCategoriesAdmin = lazy(
  () => import('./pages/ContractCategoriesAdmin'),
);
const RenewalsPage = lazy(() => import('./pages/RenewalsPage'));
const BudgetPage = lazy(() => import('./pages/BudgetPage'));
const ProcurementCreate = lazy(() => import('./pages/ProcurementCreate'));
const ProcurementDetail = lazy(() => import('./pages/ProcurementDetail'));
const PaymentTermsAdmin = lazy(() => import('./pages/PaymentTermsAdmin'));
const RenewalSettingsAdmin = lazy(() => import('./pages/RenewalSettingsAdmin'));
const VendorTypesAdmin = lazy(() => import('./pages/VendorTypesAdmin'));
const VmManufacturerAdmin = lazy(() => import('./pages/VmManufacturerAdmin'));
const ApprovalRulesAdmin = lazy(() => import('./pages/ApprovalRulesAdmin'));
const CapexSettingsAdmin = lazy(() => import('./pages/CapexSettingsAdmin'));
const RiskQuestionnaireAdmin = lazy(
  () => import('./pages/RiskQuestionnaireAdmin'),
);
const AssetsList = lazy(() => import('./pages/AssetsList'));
const AssetDetail = lazy(() => import('./pages/AssetDetail'));
const POList = lazy(() => import('./pages/POList'));
const PODetail = lazy(() => import('./pages/PODetail'));
const RFQDetail = lazy(() => import('./pages/RFQDetail'));
const MyReceipts = lazy(() => import('./pages/MyReceipts'));
const ExpenseSubmit = lazy(() => import('./pages/ExpenseSubmit'));
const ExpenseDetail = lazy(() => import('./pages/ExpenseDetail'));
const ReconcileQueue = lazy(() => import('./pages/ReconcileQueue'));
const ExpenseCategoriesAdmin = lazy(() => import('./pages/ExpenseCategoriesAdmin'));

export const slug = 'vm';

// NB: 'vendors/new', 'contracts/new' etc. MUST land before
// 'vendors/:id' / 'contracts/:id' or React Router's /:id wildcard
// will swallow the literal routes.
export const routes = [
  { path: 'my-vendors', element: MyVendors },
  { path: 'vendors/new', element: VendorIntake },
  { path: 'vendors', element: VendorList },
  { path: 'vendors/:id', element: VendorDetail },
  { path: 'contracts/new', element: ContractCreate },
  { path: 'contracts', element: ContractList },
  { path: 'contracts/:id', element: ContractDetail },
  { path: 'renewals', element: RenewalsPage },
  { path: 'budget', element: BudgetPage },
  { path: 'procurement/new', element: ProcurementCreate },
  { path: 'procurement/:id', element: ProcurementDetail },
  { path: 'procurement', element: ProcurementQueue },
  { path: 'admin/classifications', element: DataClassifications },
  { path: 'admin/cadences', element: ClassificationCadenceAdmin },
  { path: 'admin/contract-categories', element: ContractCategoriesAdmin },
  { path: 'admin/payment-terms', element: PaymentTermsAdmin },
  { path: 'admin/renewal-settings', element: RenewalSettingsAdmin },
  { path: 'admin/vendor-types', element: VendorTypesAdmin },
  { path: 'manufacturers', element: VmManufacturerAdmin },
  { path: 'admin/approval-rules', element: ApprovalRulesAdmin },
  { path: 'admin/capex-settings', element: CapexSettingsAdmin },
  { path: 'admin/risk-questionnaire', element: RiskQuestionnaireAdmin },
  { path: 'assets', element: AssetsList },
  { path: 'assets/:id', element: AssetDetail },
  { path: 'pos', element: POList },
  { path: 'pos/:id', element: PODetail },
  { path: 'rfqs/:id', element: RFQDetail },
  // VM-M6 expenses
  { path: 'expenses/mine', element: MyReceipts },
  { path: 'expenses/new', element: ExpenseSubmit },
  { path: 'expenses', element: ReconcileQueue },
  { path: 'expenses/:id', element: ExpenseDetail },
  { path: 'admin/expense-categories', element: ExpenseCategoriesAdmin },
];
